import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { AudienceSummary } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

export function AudienceSubaudiencesPanel({
  audienceId,
  isSubAudience,
  childrenCount,
  rollupLeadCount,
}: {
  audienceId: string;
  isSubAudience: boolean;
  childrenCount: number;
  rollupLeadCount?: number;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [copyFilters, setCopyFilters] = useState(true);

  const childrenQ = useQuery({
    queryKey: ['audience-children', audienceId],
    enabled: !isSubAudience,
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(`/api/audiences/${audienceId}/children`),
  });

  const split = useMutation({
    mutationFn: () =>
      apiFetch<{ audience: AudienceSummary }>(`/api/audiences/${audienceId}/split`, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          copy_segment_definition: copyFilters,
        }),
      }),
    onSuccess: (data) => {
      setName('');
      setDescription('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['audience-children', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audiences'] });
      if (data.audience?.id) navigate(`/app/doelgroepen/${data.audience.id}`);
    },
  });

  if (isSubAudience) {
    return (
      <p className={styles.mutedSmall}>
        Dit is een subdoelgroep (max. 1 niveau). Maak geen nested splits vanaf hier.
      </p>
    );
  }

  return (
    <section className={styles.card}>
      <h2>Subdoelgroepen</h2>
      <p className={styles.mutedSmall}>
        Split = clone filters + kopie van contacten. Rollup over parent + kinderen:{' '}
        <strong>{rollupLeadCount ?? '—'}</strong> contacten · {childrenCount} subdoelgroep(en).
      </p>

      {childrenQ.isLoading ? <LoadingState /> : null}

      {(childrenQ.data?.audiences.length ?? 0) > 0 ? (
        <ul className={styles.list}>
          {childrenQ.data!.audiences.map((c) => (
            <li key={c.id}>
              <Link to={`/app/doelgroepen/${c.id}`}>{c.name}</Link>
              <span className={styles.mutedSmall}>
                {' '}
                · {c.lead_count} contacten · {c.status}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <StateMessage title="Geen subdoelgroepen">Split om een subset te testen of targeten.</StateMessage>
      )}

      <Button type="button" variant="secondary" onClick={() => setOpen((v) => !v)}>
        {open ? 'Annuleren' : 'Subdoelgroep spliten'}
      </Button>

      {open ? (
        <form
          className={styles.formGrid}
          style={{ marginTop: 'var(--space-md)' }}
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            split.mutate();
          }}
        >
          <Field label="Naam subdoelgroep">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Omschrijving">
            <TextArea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </Field>
          <label>
            <input
              type="checkbox"
              checked={copyFilters}
              onChange={(e) => setCopyFilters(e.target.checked)}
            />{' '}
            Segmentdefinitie overnemen van parent
          </label>
          <p className={styles.mutedSmall}>
            Alle contacten van deze doelgroep worden gekopieerd (subset-selectie volgt in een latere
            iteratie).
          </p>
          <Button type="submit" disabled={!name.trim() || split.isPending}>
            Split aanmaken
          </Button>
        </form>
      ) : null}
    </section>
  );
}
